import React, { useMemo, useState } from 'react';
import { Card, Col, Container, Row, Table } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title
} from 'chart.js';
import { Pie, Line } from 'react-chartjs-2';
import { format, parse } from 'date-fns';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement, Title);

const PRODUCT_PERFORMANCE = [
  {
    product: 'Payout API',
    transactions: 9860,
    revenue: '₹78,42,360',
    commission: '₹3,92,118',
    growth: '+14.2%',
    growthType: 'positive',
    icon: 'bi bi-send-fill',
    color: 'primary'
  },
  {
    product: 'Recharge API',
    transactions: 8125,
    revenue: '₹26,54,980',
    commission: '₹1,32,749',
    growth: '+9.5%',
    growthType: 'positive',
    icon: 'bi bi-phone',
    color: 'success'
  },
  {
    product: 'BBPS API',
    transactions: 6540,
    revenue: '₹48,16,430',
    commission: '₹2,40,821',
    growth: '+12.8%',
    growthType: 'positive',
    icon: 'bi bi-receipt',
    color: 'info'
  },
  {
    product: 'AEPS API',
    transactions: 5210,
    revenue: '₹32,74,510',
    commission: '₹1,63,725',
    growth: '+7.1%',
    growthType: 'positive',
    icon: 'bi bi-credit-card',
    color: 'warning'
  },
  {
    product: 'DMT API',
    transactions: 4375,
    revenue: '₹65,12,290',
    commission: '₹3,25,614',
    growth: '-3.4%',
    growthType: 'negative',
    icon: 'bi bi-arrow-left-right',
    color: 'danger'
  }
];

const TOP_OPERATORS = [
  { name: 'InstantPay', transactions: 3580, share: '34%' },
  { name: 'PaySprint', transactions: 2975, share: '28%' },
  { name: 'M2P', transactions: 1980, share: '19%' },
  { name: 'Setu', transactions: 1420, share: '13%' },
  { name: 'Others', transactions: 865, share: '6%' }
];

const DATE_WISE_TRANSACTIONS = [
  { label: '11 Nov 2025', txnCount: 1425, successRate: '97.6%', revenue: '₹11,62,540', avgLatency: '214 ms' },
  { label: '10 Nov 2025', txnCount: 1382, successRate: '96.9%', revenue: '₹10,98,730', avgLatency: '221 ms' },
  { label: '09 Nov 2025', txnCount: 1324, successRate: '95.8%', revenue: '₹10,45,180', avgLatency: '238 ms' },
  { label: '08 Nov 2025', txnCount: 1288, successRate: '97.1%', revenue: '₹10,12,460', avgLatency: '219 ms' },
  { label: '07 Nov 2025', txnCount: 1215, successRate: '94.5%', revenue: '₹9,74,230', avgLatency: '246 ms' },
  { label: '06 Nov 2025', txnCount: 1168, successRate: '95.2%', revenue: '₹9,38,410', avgLatency: '232 ms' },
  { label: '05 Nov 2025', txnCount: 1096, successRate: '93.8%', revenue: '₹8,94,560', avgLatency: '257 ms' }
];

const ApiPartnerDashboard = () => {
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;

  const dateWiseTransactions = useMemo(
    () =>
      DATE_WISE_TRANSACTIONS.map((entry) => ({
        ...entry,
        date: parse(entry.label, 'dd MMM yyyy', new Date()),
        successRateValue: parseFloat(entry.successRate.replace('%', ''))
      })),
    []
  );

  const filteredTransactions = useMemo(() => {
    if (!startDate || !endDate) {
      return dateWiseTransactions;
    }

    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);

    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);

    return dateWiseTransactions.filter((entry) => entry.date >= start && entry.date <= end);
  }, [dateWiseTransactions, startDate, endDate]);

  const productPieData = useMemo(
    () => ({
      labels: PRODUCT_PERFORMANCE.map((product) => product.product),
      datasets: [
        {
          data: PRODUCT_PERFORMANCE.map((product) => product.transactions),
          backgroundColor: ['#0d6efd', '#198754', '#0dcaf0', '#fd7e14', '#dc3545'],
          borderWidth: 2,
          borderColor: '#ffffff'
        }
      ]
    }),
    []
  );

  const productPieOptions = useMemo(
    () => ({
      responsive: true,
      plugins: {
        legend: {
          position: 'bottom'
        },
        tooltip: {
          callbacks: {
            label: (context) => {
              const product = PRODUCT_PERFORMANCE[context.dataIndex];
              return `${product.product}: ${product.transactions.toLocaleString()} txns (${product.revenue} revenue)`;
            }
          }
        }
      }
    }),
    []
  );

  const lineChartData = useMemo(
    () => ({
      labels: filteredTransactions.map((entry) => format(entry.date, 'dd MMM')),
      datasets: [
        {
          label: 'Transactions',
          data: filteredTransactions.map((entry) => entry.txnCount),
          borderColor: '#0d6efd',
          backgroundColor: 'rgba(13, 110, 253, 0.15)',
          tension: 0.4,
          fill: true,
          yAxisID: 'y'
        },
        {
          label: 'Success Rate %',
          data: filteredTransactions.map((entry) => entry.successRateValue),
          borderColor: '#198754',
          backgroundColor: 'rgba(25, 135, 84, 0.15)',
          tension: 0.4,
          fill: false,
          yAxisID: 'y1'
        }
      ]
    }),
    [filteredTransactions]
  );

  const lineChartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index',
        intersect: false
      },
      plugins: {
        legend: {
          position: 'bottom'
        },
        tooltip: {
          callbacks: {
            label: (context) => {
              const entry = filteredTransactions[context.dataIndex];
              if (context.dataset.label === 'Success Rate %') {
                return `Success Rate: ${entry.successRate}`;
              }
              if (context.dataset.label === 'Transactions') {
                return `Transactions: ${entry.txnCount.toLocaleString()}`;
              }
              return context.formattedValue;
            },
            afterLabel: (context) => {
              const entry = filteredTransactions[context.dataIndex];
              return `Revenue: ${entry.revenue}\nAvg Latency: ${entry.avgLatency}`;
            }
          }
        }
      },
      scales: {
        y: {
          type: 'linear',
          display: true,
          position: 'left',
          title: {
            display: true,
            text: 'Transactions'
          },
          grid: {
            color: 'rgba(13, 110, 253, 0.1)'
          }
        },
        y1: {
          type: 'linear',
          display: true,
          position: 'right',
          title: {
            display: true,
            text: 'Success Rate (%)'
          },
          grid: {
            drawOnChartArea: false
          },
          min: 85,
          max: 100,
          ticks: {
            callback: (value) => `${value}%`
          }
        }
      }
    }),
    [filteredTransactions]
  );

  return (
    <div className="api-partner-dashboard">
      <Container fluid className="py-4">
        <div className="d-flex flex-column flex-md-row justify-content-between gap-3 align-items-md-center mb-4">
          <div>
            <h3 className="mb-2">API Operations Command Center</h3>
          </div>
        </div>

        <Row className="g-3 mb-3">
          <Col xl={8}>
            <Card className="h-100 border-0 shadow-sm">
              <Card.Header>
                <h5 className="mb-1">Product-wise Transaction Details</h5>
                <small className="text-muted">Distribution of overall transactions by product</small>
              </Card.Header>
              <Card.Body>
                <Row className="g-4 align-items-center">
                  <Col lg={6}>
                    <div style={{ minHeight: 320 }}>
                      <Pie data={productPieData} options={productPieOptions} />
                    </div>
                  </Col>
                  <Col lg={6}>
                    <div className="table-responsive">
                      <Table hover size="sm" className="mb-0 align-middle">
                        <thead className="table-light text-nowrap">
                          <tr>
                            <th>Product</th>
                            <th className="text-end">Transactions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {PRODUCT_PERFORMANCE.map((product) => (
                            <tr key={product.product}>
                              <td className="fw-semibold">
                                <i className={`${product.icon} text-${product.color} me-2`}></i>
                                {product.product}
                              </td>
                              <td className="text-end">{product.transactions.toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                    <div className="pt-3 border-top mt-3">
                      {PRODUCT_PERFORMANCE.map((product) => (
                        <div key={product.product} className="d-flex justify-content-between small text-muted mb-2">
                          <span>{product.product}</span>
                          <span>
                            Rev: {product.revenue} · Comm: {product.commission} ·{' '}
                            <span className={`text-${product.growthType === 'positive' ? 'success' : 'danger'}`}>
                              {product.growth}
                            </span>
                          </span>
                        </div>
                      ))}
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
          <Col xl={4}>
            <Card className="h-100 border-0 shadow-sm">
              <Card.Header>
                <h5 className="mb-0">Top Performing Partners</h5>
                <small className="text-muted">Distribution across integrated operators</small>
              </Card.Header>
              <Card.Body className="d-flex flex-column gap-4">
                {TOP_OPERATORS.map((operator) => (
                  <div key={operator.name}>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div>
                        <p className="mb-0 fw-semibold">{operator.name}</p>
                        <small className="text-muted">{operator.transactions} txns</small>
                      </div>
                      <span className="badge bg-primary-subtle text-primary fw-semibold">{operator.share}</span>
                    </div>
                    <div className="progress" style={{ height: '8px' }}>
                      <div
                        className="progress-bar bg-primary"
                        role="progressbar"
                        style={{ width: operator.share }}
                        aria-valuenow={parseInt(operator.share, 10)}
                        aria-valuemin="0"
                        aria-valuemax="100"
                      ></div>
                    </div>
                  </div>
                ))}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="g-3">
          <Col>
            <Card className="border-0 shadow-sm">
              <Card.Header className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2">
                <div>
                  <h5 className="mb-1">Date-wise Transaction Performance</h5>
                  <small className="text-muted">Daily trends with success rate and latency insights</small>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted small text-nowrap">Filter by date</span>
                  <DatePicker
                    selectsRange
                    startDate={startDate}
                    endDate={endDate}
                    onChange={(range) => setDateRange(range || [null, null])}
                    isClearable
                    placeholderText="Select range"
                    maxDate={new Date()}
                    className="form-control form-control-sm text-center"
                  />
                </div>
              </Card.Header>
              <Card.Body>
                <div style={{ minHeight: 360 }}>
                  {filteredTransactions.length > 0 ? (
                    <Line data={lineChartData} options={lineChartOptions} />
                  ) : (
                    <div className="d-flex flex-column justify-content-center align-items-center h-100 text-muted">
                      <i className="bi bi-graph-up fs-1 mb-2"></i>
                      <p className="mb-0">No transactions found for the selected date range.</p>
                    </div>
                  )}
                </div>

                {filteredTransactions.length > 0 && (
                  <div className="table-responsive mt-4">
                    <Table hover className="mb-0 text-nowrap align-middle">
                      <thead className="table-light">
                        <tr>
                          <th>Date</th>
                          <th>Total Transactions</th>
                          <th>Success Rate</th>
                          <th>Revenue</th>
                          <th>Average Latency</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredTransactions.map((entry) => (
                          <tr key={entry.label}>
                            <td className="fw-semibold">{entry.label}</td>
                            <td>{entry.txnCount.toLocaleString()}</td>
                            <td className="text-success fw-semibold">{entry.successRate}</td>
                            <td className="fw-semibold">{entry.revenue}</td>
                            <td>{entry.avgLatency}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ApiPartnerDashboard;

